<template>
    <div class="mv-mitglieder-detail">
        <h2>{{ heading }}</h2>
        <section class="mv-mitglieder-info">
            <b-row>
                <b-col cols="9">
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="ID:"
                        label-for="mId"
                        :label-size="size"
                    >
                        <b-form-input
                            id="mId"
                            v-model="data.id"
                            :size="size"
                            :disabled="isBusy || true"
                        />
                    </b-form-group>
                    <hr />
                    <h4>Zuweisungen</h4>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Status:"
                        label-for="mStatus"
                        :label-size="size"
                    >
                        <template v-slot:label>
                            Status:
                            <span class="text-danger">*</span>
                        </template>
                        <b-form-select
                            id="mStatus"
                            v-model="data.status"
                            :options="status.items"
                            :size="size"
                            :disabled="status.isBusy"
                        />
                    </b-form-group>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Statuserweiterungen:"
                        label-for="mStatusErweiterung"
                        :label-size="size"
                    >
                        <CheckboxSelect
                            id="mStatusErweiterung"
                            :options="statuserweiterung.items"
                            :selectSize="selectSize"
                            :size="size"
                            :isBusy="isBusy || statuserweiterung.isBusy"
                            :sortActive="true"
                            v-model="data.statuserweiterungen"
                        />
                    </b-form-group>
                    <b-form-group
                        class="mv-mitglieder-leitungsteam"
                        :label-cols-sm="labelCols"
                        label="Leitungsteam:"
                        label-for="mLeitungsteamGruppe"
                        :label-size="size"
                    >
                        <div v-if="isNew" class="alert alert-warning">
                            Beim Anlegen eines neuen Mitglieds, müssen zuerst
                            die Stammdaten gespeichert werden, und in einem
                            zweiten Schritt können dann die Leitungsteamdaten
                            hinterlegt werden. Sobald Sie fertig mit der eingabe
                            der Grunddaten sind, können Sie nach einem Druck auf
                            den Speichern-Button direkt die Leitungsteamdaten
                            eingeben.
                        </div>
                        <div
                            v-else-if="leitungsteam.relations.busy"
                            class="leitungsteam-loading"
                        >
                            <Spinner small class="spinner" />
                            <p class="small">
                                Leitungsteamdaten werden geladen
                            </p>
                        </div>
                        <template v-else>
                            <EndlessList
                                v-model="leitungsteam.relations.items"
                                additionalClass="contact-endless-list"
                                :busy="leitungsteam.relations.busy || isBusy"
                                :newItemBoilerplate="
                                    leitungsteam.relations.newItem
                                "
                                :hideNewButton="!checkRight('write')"
                                :hideDeleteButton="!checkRight('delete')"
                                newButtonLabel="Leitungsteamposition hinzufügen"
                                :size="size"
                            >
                                <template v-slot:default="{ item: item }">
                                    <b-input-group
                                        class="mv-leitungsteam-group"
                                    >
                                        <b-select
                                            id="mLeitungsteamGruppe"
                                            :size="size"
                                            :disabled="
                                                isBusy ||
                                                    leitungsteam.groups.busy
                                            "
                                            :options="
                                                leitungsteam.groups.options
                                            "
                                            v-model="item.leitungsteam_group_id"
                                        />
                                        <b-input
                                            id="mLeitungsteamBezeichnung"
                                            :size="size"
                                            :disabled="isBusy"
                                            placeholder="Positionsbeschreibung"
                                            v-model="item.description"
                                        />
                                        <b-select
                                            id="mLeitungsteamRolle"
                                            :options="
                                                leitungsteam.roles.options
                                            "
                                            :size="size"
                                            :disabled="
                                                isBusy ||
                                                    leitungsteam.roles.busy
                                            "
                                            v-model="item.leitungsteam_role_id"
                                        />
                                        <b-input
                                            id="mLeitungsteamPos"
                                            class="text-right"
                                            :size="size"
                                            :disabled="isBusy"
                                            placeholder="Reihenfolge"
                                            v-model="item.pos"
                                        />
                                    </b-input-group>
                                </template>
                            </EndlessList>
                        </template>
                    </b-form-group>
                </b-col>
                <b-col cols="3">
                    <template v-if="isNew">
                        <div class="alert alert-warning">
                            <font-awesome-icon
                                icon="camera"
                                class="d-block mx-auto mt-1 mb-3 fa-2x"
                            />Ein Mitgliedsbild kann nach dem Anlegen des neuen
                            Mitglieds hinzugefügt werden.
                        </div>
                    </template>
                    <template v-else>
                        <MitgliederImage v-model="data.photo_id" />
                    </template>
                    <div
                        class="
                            text-right bg-secondary text-white p-2 text-small
                    "
                    >
                        <font-awesome-icon icon="camera" />
                        Foto freigeben:
                        <input
                            id="mFotoFrei"
                            type="checkbox"
                            v-model="data.foto_frei"
                            true-value="1"
                            false-value="0"
                            :disabled="isBusy"
                        />
                    </div>
                </b-col>
            </b-row>
        </section>
        <hr />
        <section class="mv-mitglieder-data">
            <h4>Persönliche Daten</h4>
            <template v-if="isNew && possibleDuplicateInName">
                <b-alert variant="warning" :show="true">
                    Das System hat schon einen Mitgliedsdatensatz zu dem
                    angegebenen Vor- und Nachnamen erkannt. Um zu vermeiden,
                    redundante Datensätze der selben physischen Person
                    vorzuhalten, überprüfen Sie bitte, ob es sich bei dem
                    anzulegenden Mitglied wirklich um ein NEUES (und/oder
                    namensgleiches) Mitglied handelt, oder ob es ausreicht den
                    vorhandenen Datensatz dieses Mitglieds zu erweitern.
                </b-alert>
            </template>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Anrede:"
                label-for="mAnrede"
                :label-size="size"
            >
                <b-form-select
                    id="mAnrede"
                    v-model="data.anrede"
                    :options="anrede.items"
                    :size="size"
                    :disabled="isBusy"
                />
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Titel:"
                label-for="mTitel"
                :label-size="size"
            >
                <b-form-input
                    id="mTitel"
                    v-model="data.titel"
                    :size="size"
                    :disabled="isBusy"
                />
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Vor- / Künstlername:"
                label-for="mVorname"
                :label-size="size"
            >
                <template v-slot:label>Vor- / Künstlername:</template>
                <b-form-input
                    id="mVorname"
                    v-model="data.vorname"
                    :size="size"
                    :disabled="isBusy"
                    :state="ioFields.vorname.valid"
                />
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label-for="mName"
                :label-size="size"
                :state="ioFields.name.valid"
            >
                <template v-slot:label>
                    Name / Künstlername 1:
                    <span class="text-danger">*</span>
                </template>
                <template v-slot:invalid-feedback>{{
                    ioFields.name.invalidFeedback
                }}</template>
                <b-form-input
                    id="mName"
                    v-model="data.name"
                    :size="size"
                    :disabled="isBusy"
                    :state="ioFields.name.valid"
                />
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Name / Künstlername 2:"
                label-for="mName2"
                :label-size="size"
            >
                <b-form-input
                    id="mName2"
                    v-model="data.name2"
                    :size="size"
                    :disabled="isBusy"
                />
            </b-form-group>
            <b-form-group
                description="Hier wird der richtige / bürgerliche Name des Künstlers eingetragen"
                :label-cols-sm="labelCols"
                label="Name 3:"
                label-for="mName3"
                :label-size="size"
            >
                <b-form-input
                    id="mName3"
                    v-model="data.name3"
                    :size="size"
                    :disabled="isBusy"
                />
            </b-form-group>
            <b-form-group
                description="Berufsbezeichnung"
                :label-cols-sm="labelCols"
                label="Beruf:"
                label-for="mBeruf"
                :label-size="size"
            >
                <b-form-input
                    id="mBeruf"
                    v-model="data.beruf"
                    :size="size"
                    :disabled="isBusy"
                />
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Strasse:"
                label-for="mStrasse"
                :label-size="size"
            >
                <b-input-group :size="size">
                    <b-form-input
                        id="mStrasse"
                        type="text"
                        aria-label="Text input with checkbox"
                        v-model="data.strasse"
                        :disabled="isBusy"
                    />
                    <b-input-group-append is-text>
                        <input
                            type="checkbox"
                            true-value="1"
                            false-value="0"
                            aria-label="Checkbox for following text input"
                            v-model="data.adresse_frei"
                            :disabled="isBusy"
                        />
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Postfach:"
                label-for="mPostfach"
                :label-size="size"
            >
                <b-input-group :size="size">
                    <b-form-input
                        id="mPostfach"
                        type="text"
                        aria-label="Text input with checkbox"
                        v-model="data.postfach"
                        :disabled="isBusy"
                    />
                    <b-input-group-append is-text>
                        <input
                            type="checkbox"
                            true-value="1"
                            false-value="0"
                            aria-label="Checkbox for following text input"
                            v-model="data.adresse_frei"
                            :disabled="isBusy"
                        />
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="PLZ-Ort:"
                label-for="mPlzOrt"
                :label-size="size"
            >
                <b-row>
                    <b-col cols="4">
                        <b-form-input
                            id="mPlz"
                            v-model="data.plz"
                            :size="size"
                            :disabled="isBusy"
                            placeholder="PLZ"
                        />
                    </b-col>
                    <b-col cols="8">
                        <b-form-input
                            id="mOrt"
                            v-model="data.ort"
                            :size="size"
                            :disabled="isBusy"
                            placeholder="Ort"
                        />
                    </b-col>
                </b-row>
            </b-form-group>
        </section>
        <hr />
        <section class="mv-mitglieder-contact">
            <h4>Kontakt</h4>
            <div v-if="isNew" class="alert alert-warning">
                Beim Anlegen eines neuen Mitglieds, müssen zuerst die Stammdaten
                gespeichert werden, und in einem zweiten Schritt können dann die
                Kontaktdaten hinterlegt werden. Sobald Sie fertig mit der
                eingabe der Grunddaten sind, können Sie nach einem Druck auf den
                Speichern-Button direkt die Kontaktdaten eingeben.
            </div>
            <div v-else-if="contact.busy" class="contact-loading">
                <Spinner small class="spinner" />Kontakte werden geladen
            </div>
            <template v-else>
                <EndlessList
                    v-model="contact.items"
                    additionalClass="contact-endless-list"
                    :busy="contact.busy || isBusy"
                    :newItemBoilerplate="contact.newItem"
                    :hideNewButton="!checkRight('write')"
                    :hideDeleteButton="!checkRight('delete')"
                    :size="size"
                >
                    <template v-slot:default="{ item: item }">
                        <template v-if="item.id.toString().includes('new_')">
                            <b-row :key="item.id" class="new-contact-field">
                                <b-col :cols="labelCols">
                                    <b-input
                                        type="text"
                                        :size="size"
                                        v-model="item.description"
                                        placeholder="Bezeichnung"
                                        :disabled="contact.busy || isBusy"
                                    />
                                </b-col>
                                <b-col class="pl-2">
                                    <b-input-group :size="size">
                                        <b-input-group-prepend>
                                            <b-select
                                                :size="size"
                                                v-model="item.type"
                                                :disabled="
                                                    contact.busy || isBusy
                                                "
                                                @change="
                                                    modifyContactData(item)
                                                "
                                            >
                                                <option :value="null">
                                                    Kontakttyp auswählen...
                                                </option>
                                                <option value="phone"
                                                    >Telefon</option
                                                >
                                                <option value="fax">Fax</option>
                                                <option value="mail"
                                                    >Mail</option
                                                >
                                                <option value="web"
                                                    >Webseite</option
                                                >
                                                <option value="other"
                                                    >Andere</option
                                                >
                                            </b-select>
                                        </b-input-group-prepend>
                                        <b-form-input
                                            id="contact_new"
                                            type="text"
                                            v-model="item.value"
                                            placeholder="Kontaktdaten"
                                            :disabled="contact.busy || isBusy"
                                        />
                                        <b-input-group-append>
                                            <b-select
                                                :size="size"
                                                v-model="item.preferred"
                                                :disabled="
                                                    contact.busy || isBusy
                                                "
                                            >
                                                <option :value="null"
                                                    >Bevorzugt?</option
                                                >
                                                <option :value="1"
                                                    >bevorzugt</option
                                                >
                                                <option :value="0"
                                                    >normal</option
                                                >
                                            </b-select>
                                        </b-input-group-append>
                                        <b-input-group-append is-text>
                                            <input
                                                type="checkbox"
                                                true-value="1"
                                                false-value="0"
                                                v-model="item.open"
                                                :disabled="
                                                    contact.busy || isBusy
                                                "
                                            />
                                        </b-input-group-append>
                                    </b-input-group>
                                </b-col>
                            </b-row>
                        </template>
                        <template v-else>
                            <b-form-group
                                :key="item.id"
                                :label-cols-sm="labelCols"
                                :label="item.description"
                                :label-for="'contact' + item.id"
                                :label-size="size"
                            >
                                <b-input-group :size="size">
                                    <b-input-group-prepend>
                                        <b-input-group-text>
                                            <template
                                                v-if="item.type === 'phone'"
                                            >
                                                <font-awesome-icon
                                                    icon="phone"
                                                />
                                            </template>
                                            <template
                                                v-else-if="item.type === 'fax'"
                                            >
                                                <font-awesome-icon icon="fax" />
                                            </template>
                                            <template
                                                v-else-if="item.type === 'mail'"
                                            >
                                                <font-awesome-icon
                                                    icon="envelope"
                                                />
                                            </template>
                                            <template
                                                v-else-if="item.type === 'web'"
                                            >
                                                <font-awesome-icon
                                                    icon="globe-europe"
                                                />
                                            </template>
                                            <template v-else>
                                                <font-awesome-icon
                                                    icon="id-card"
                                                />
                                            </template>
                                        </b-input-group-text>
                                    </b-input-group-prepend>
                                    <b-form-input
                                        :id="'contact' + item.id"
                                        type="text"
                                        v-model="item.value"
                                        :disabled="contact.busy || isBusy"
                                    />
                                    <template v-if="item.type === 'mail'">
                                        <b-input-group-append>
                                            <b-button
                                                v-if="
                                                    checkRight('write') &&
                                                        mattermost.initial
                                                "
                                                variant="outline-warning"
                                                :disabled="
                                                    contact.busy || isBusy
                                                "
                                                @click="
                                                    addMattermostFromMail(item)
                                                "
                                                >Zu Mattermost
                                                hinzufügen</b-button
                                            >
                                            <b-button
                                                variant="outline-primary"
                                                :disabled="
                                                    contact.busy || isBusy
                                                "
                                                :href="'mailto:' + item.value"
                                                target="_blank"
                                                >Email senden</b-button
                                            >
                                        </b-input-group-append>
                                    </template>
                                    <template v-else-if="item.type === 'web'">
                                        <b-input-group-append>
                                            <b-button
                                                variant="outline-primary"
                                                :disabled="
                                                    contact.busy || isBusy
                                                "
                                                :href="item.value"
                                                target="_blank"
                                                >Webseite besuchen</b-button
                                            >
                                        </b-input-group-append>
                                    </template>
                                    <b-input-group-append>
                                        <b-select
                                            :size="size"
                                            v-model="item.preferred"
                                            :disabled="contact.busy || isBusy"
                                        >
                                            <option :value="null"
                                                >Bevorzugt?</option
                                            >
                                            <option :value="1"
                                                >bevorzugt</option
                                            >
                                            <option :value="0">normal</option>
                                        </b-select>
                                    </b-input-group-append>
                                    <b-input-group-append is-text>
                                        <input
                                            type="checkbox"
                                            true-value="1"
                                            false-value="0"
                                            v-model="item.open"
                                            :disabled="contact.busy || isBusy"
                                        />
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>
                        </template>
                    </template>
                </EndlessList>
                <template v-if="showOldContacts">
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Telefon (privat):"
                        label-for="mPrivat"
                        :label-size="size"
                    >
                        <b-input-group :size="size">
                            <b-form-input
                                id="mPrivat"
                                type="text"
                                v-model="data.privat"
                                :disabled="isBusy"
                            />
                            <b-input-group-append is-text>
                                <input
                                    type="checkbox"
                                    true-value="1"
                                    false-value="0"
                                    v-model="data.privat_frei"
                                    :disabled="isBusy"
                                />
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Telefon (Büro):"
                        label-for="mBuero"
                        :label-size="size"
                    >
                        <b-input-group :size="size">
                            <b-form-input
                                id="mBuero"
                                type="text"
                                v-model="data.buero"
                                :disabled="isBusy"
                            />
                            <b-input-group-append is-text>
                                <input
                                    type="checkbox"
                                    true-value="1"
                                    false-value="0"
                                    v-model="data.buero_frei"
                                    :disabled="isBusy"
                                />
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Telefon (mobil):"
                        label-for="mMobil"
                        :label-size="size"
                    >
                        <b-input-group :size="size">
                            <b-form-input
                                id="mMobil"
                                type="text"
                                v-model="data.mobil"
                                :disabled="isBusy"
                            />
                            <b-input-group-append is-text>
                                <input
                                    type="checkbox"
                                    true-value="1"
                                    false-value="0"
                                    v-model="data.mobil_frei"
                                    :disabled="isBusy"
                                />
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Fax:"
                        label-for="mFax"
                        :label-size="size"
                    >
                        <b-input-group :size="size">
                            <b-form-input
                                id="mFax"
                                type="text"
                                v-model="data.fax"
                                :disabled="isBusy"
                            />
                            <b-input-group-append is-text>
                                <input
                                    type="checkbox"
                                    true-value="1"
                                    false-value="0"
                                    v-model="data.fax_frei"
                                    :disabled="isBusy"
                                />
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Email:"
                        label-for="mEmail"
                        :label-size="size"
                    >
                        <b-input-group :size="size">
                            <b-form-input
                                id="mEmail"
                                type="text"
                                v-model="data.email"
                                :disabled="isBusy"
                            />
                            <b-input-group-append>
                                <b-button
                                    text="Button"
                                    variant="outline-primary"
                                    :disabled="isBusy"
                                    :href="'mailto:' + data.email"
                                    >Email senden</b-button
                                >
                            </b-input-group-append>
                            <b-input-group-append is-text>
                                <input
                                    type="checkbox"
                                    true-value="1"
                                    false-value="0"
                                    v-model="data.email_frei"
                                    :disabled="isBusy"
                                />
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Email 2:"
                        label-for="mEmail2"
                        :label-size="size"
                    >
                        <b-input-group :size="size">
                            <b-form-input
                                id="mEmail2"
                                type="text"
                                v-model="data.email2"
                                :disabled="isBusy"
                            />
                            <b-input-group-append>
                                <b-button
                                    text="Button"
                                    variant="outline-primary"
                                    :disabled="isBusy"
                                    :href="'mailto:' + data.email2"
                                    >Email senden</b-button
                                >
                            </b-input-group-append>
                            <b-input-group-append is-text>
                                <input
                                    type="checkbox"
                                    true-value="1"
                                    false-value="0"
                                    v-model="data.email2_frei"
                                    :disabled="isBusy"
                                />
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Website:"
                        label-for="mWebsite"
                        :label-size="size"
                    >
                        <b-input-group :size="size">
                            <b-form-input
                                id="mWebsite"
                                type="text"
                                v-model="data.webseite"
                                :disabled="isBusy"
                            />
                            <b-input-group-append>
                                <b-button
                                    variant="outline-primary"
                                    :disabled="isBusy"
                                    :href="data.webseite"
                                    target="_blank"
                                    >Website besuchen</b-button
                                >
                            </b-input-group-append>
                            <b-input-group-append is-text>
                                <input
                                    type="checkbox"
                                    true-value="1"
                                    false-value="0"
                                    aria-label="Checkbox for following text input"
                                    v-model="data.webseite_frei"
                                    :disabled="isBusy"
                                />
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </template>
            </template>
        </section>
        <hr />
        <section class="mv-mitglieder-mattermost">
            <h4>Mattermost</h4>
            <div v-if="isNew" class="alert alert-warning">
                Beim Anlegen eines neuen Mitglieds, müssen zuerst die Stammdaten
                gespeichert werden, und in einem zweiten Schritt kann dann ein
                Mattermost-Zugang hinterlegt werden. Sobald Sie fertig mit der
                eingabe der Grunddaten sind, können Sie nach einem Druck auf den
                Speichern-Button direkt den Mattermost-Zugang für dieses
                Mitglied einrichten.
            </div>
            <div v-else-if="mattermost.busy" class="mattermost-loading">
                <Spinner small class="spinner" />Mattermost-Informationen werden
                geladen
            </div>
            <template v-else>
                <div>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Registrierte E-Mailadresse:"
                        label-for="mMattermostMail"
                        :label-size="size"
                    >
                        <b-form-input
                            id="mMattermostMail"
                            v-model="mattermost.email"
                            :disabled="
                                !mattermost.initial || isBusy || mattermost.busy
                            "
                            :size="size"
                        />
                    </b-form-group>
                    <b-form-group
                        :label-cols-sm="labelCols"
                        label="Zugang aktivieren:"
                        label-for="mMattermostStatus"
                        :label-size="size"
                    >
                        <b-checkbox
                            id="mMattermostStatus"
                            v-model="mattermost.status"
                            :disabled="isBusy || mattermost.busy"
                            switch
                        />
                    </b-form-group>
                </div>
            </template>
        </section>
        <hr />
        <section class="mv-mitglieder-dates">
            <h4>Daten</h4>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Geburtsdatum:"
                label-for="mGeb"
                :label-size="size"
            >
                <b-input-group :size="size">
                    <b-form-input
                        id="mGeb"
                        type="date"
                        v-model="data.geburtstag"
                        :disabled="isBusy"
                    />
                    <b-input-group-append is-text>
                        <input
                            type="checkbox"
                            true-value="1"
                            false-value="0"
                            v-model="data.geburtstag_frei"
                            :disabled="isBusy"
                        />
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Eintritt:"
                label-for="mEintritt"
                :label-size="size"
            >
                <b-input-group :size="size">
                    <b-form-input
                        id="mEintritt"
                        type="date"
                        v-model="data.eintritt"
                        :disabled="isBusy"
                    />
                    <b-input-group-append is-text>
                        <input
                            type="checkbox"
                            true-value="1"
                            false-value="0"
                            v-model="data.eintritt_frei"
                            :disabled="isBusy"
                        />
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Austritt:"
                label-for="mAustritt"
                :label-size="size"
            >
                <b-form-input
                    type="date"
                    id="mAustritt"
                    v-model="data.austritt"
                    :disabled="isBusy"
                    :size="size"
                />
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Stimmprüfung:"
                label-for="mStimmpruefung"
                :label-size="size"
            >
                <b-form-input
                    type="date"
                    id="mStimmpruefung"
                    v-model="data.stimmprfung"
                    :disabled="isBusy"
                    :size="size"
                />
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Todesdatum:"
                label-for="mTodesdatum"
                :label-size="size"
            >
                <b-form-input
                    type="date"
                    id="mTodesdatum"
                    v-model="data.todesdatum"
                    :disabled="isBusy"
                    :size="size"
                />
            </b-form-group>
        </section>
        <hr />
        <section class="mv-mitglieder-bank-account">
            <h4>Bankdaten</h4>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Bank:"
                label-for="mBank"
                :label-size="size"
            >
                <b-form-input
                    id="mBank"
                    v-model="data.bank"
                    :disabled="isBusy"
                    :size="size"
                />
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="IBAN:"
                label-for="mIban"
                :label-size="size"
            >
                <b-form-input
                    id="mIban"
                    v-model="data.swift"
                    :disabled="isBusy"
                    :size="size"
                />
            </b-form-group>
            <b-form-group
                :label-cols-sm="labelCols"
                label="BIC:"
                label-for="mBic"
                :label-size="size"
            >
                <b-form-input
                    id="mBic"
                    v-model="data.bic"
                    :disabled="isBusy"
                    :size="size"
                />
            </b-form-group>
        </section>
        <hr />
        <section class="mv-mitglieder-misc">
            <h4>Bemerkungen</h4>
            <b-form-group
                :label-cols-sm="labelCols"
                label="Bemerkung:"
                label-for="mBemerkung"
                :label-size="size"
            >
                <b-form-textarea
                    id="mBemerkung"
                    v-model="data.bemerkung_new"
                    :size="size"
                    :disabled="isBusy"
                    rows="5"
                />
            </b-form-group>
        </section>
        <hr />
        <b-button
            v-if="checkRight('write')"
            type="submit"
            class="total-btn"
            variant="primary"
            :disabled="isBusy"
            @click="dataProvide"
        >
            <template v-if="!isBusy">Speichern</template>
            <template v-else>
                <Spinner small variant="light" />
            </template>
        </b-button>
        <b-button
            type="cancel"
            href="/mitglieder"
            class="total-btn"
            variant="outline-secondary"
        >
            <template v-if="checkRight('write')">Abbrechen</template>
            <template v-else>Zurück</template>
        </b-button>
    </div>
</template>

<script>
import shortid from "shortid";
import { ApiFactory } from "@/api/ApiFactory.js";
import Toaster from "@/mixins/Toaster/Toaster";
import Rights from "@/mixins/Rights/Rights";
import MitgliederCheck from "@/mixins/MitgliederCheck/MitgliederCheck";
import Validation from "@/mixins/Validation/Validation";
import CheckboxSelect from "@/components/CheckboxSelect/CheckboxSelect";
import MitgliederImage from "@/components/MitgliederImage/MitgliederImage";
import Spinner from "@/components/Spinner/Spinner";
import EndlessList from "@/components/EndlessList/EndlessList";

import "./MitgliederDetail.scss";

const MitgliederApi = ApiFactory.get("mitglieder");
const StatuserweiterungApi = ApiFactory.get("statuserweiterung");

export default {
    name: "MitgliederDetail",
    props: ["id"],
    mixins: [Toaster, Rights, Validation, MitgliederCheck],
    components: {
        CheckboxSelect,
        MitgliederImage,
        Spinner,
        EndlessList
    },
    data() {
        return {
            data: {},
            anrede: {
                items: [
                    "Frau",
                    "Herrn",
                    "Firma",
                    "Monsieur",
                    "Madame",
                    "Mrs.",
                    "Mr.",
                    "keine Angabe"
                ]
            },
            status: {
                isBusy: false,
                items: []
            },
            statuserweiterung: {
                isBusy: false,
                items: []
            },
            contact: {
                busy: false,
                items: [],
                newItem: {
                    description: null,
                    mitglied_id: this.id,
                    open: 0,
                    preferred: 0,
                    type: null,
                    value: null
                }
            },
            leitungsteam: {
                groups: {
                    busy: false,
                    items: [],
                    options: [
                        {
                            value: null,
                            text: "Nicht im Leitungsteam"
                        }
                    ]
                },
                roles: {
                    busy: false,
                    items: [],
                    options: [{ value: null, text: "Keine Angabe" }]
                },
                relations: {
                    busy: false,
                    items: [],
                    newItem: {
                        description: null,
                        mitglied_id: this.id,
                        pos: 0,
                        leitungsteam_group_id: null,
                        leitungsteam_role_id: null
                    }
                }
            },
            mattermost: {
                busy: false,
                id: null,
                email: null,
                status: false,
                initial: false
            },
            labelCols: 2,
            selectSize: 5,
            isBusy: false,
            showOldContacts: false,
            possibleDuplicateInName: false,
            ioFields: {
                name: {},
                vorname: {},
                status: {}
            }
        };
    },
    created() {
        this.loadData();
    },
    computed: {
        heading() {
            if (this.isNew) {
                return "Neues Mitglied anlegen";
            } else {
                const data = this.data;
                return Object.keys(data).length === 0 &&
                    typeof data === "object"
                    ? this.id
                    : data.vorname + " " + data.name;
            }
        },
        size() {
            const settings = this.$store.getters.getSettings;
            return settings.size;
        },
        isNew() {
            return this.id === "new";
        }
    },
    watch: {
        "data.name": {
            async handler() {
                this.possibleDuplicateInName = await this.checkForPossibleDuplicates();
            }
        },
        id: {
            handler() {
                this.loadData();
            }
        }
    },
    methods: {
        loadData() {
            if (this.isNew) {
                this.fetch();
                this.fetchStatus();
                this.fetchStatuserweiterung();
            } else {
                this.fetch();
                this.fetchStatus();
                this.fetchStatuserweiterung();
                this.fetchContact();
                this.fetchMattermost();
                this.fetchLeitungsteamForMitglied();
                this.fetchLeitungsteamGroups();
                this.fetchLeitungsteamRoles();
            }
        },
        async fetch() {
            this.isBusy = true;
            const response = await MitgliederApi.getSingle(this.id);

            if (!response.ok) {
                // todo: Errorhandling
            }
            this.registerRights(response);
            this.cleanPayload(response.json);
            this.data = response.json;
            this.isBusy = false;
        },
        async fetchStatus() {
            this.status.isBusy = true;
            const response = await MitgliederApi.getStatus();

            if (!response.ok) {
                // todo: Errorhandling
            }

            const status = response.json.collection;
            var map = status.map(status => {
                return {
                    value: status.value,
                    text: status.value + " (" + status.name + ")"
                };
            });
            this.status.isBusy = false;
            this.status.items = map;
        },
        async fetchStatuserweiterung() {
            this.statuserweiterung.isBusy = true;
            const response = await StatuserweiterungApi.get();
            const statuserweiterung = response.json.collection;

            var map = statuserweiterung.map(se => {
                return { value: se.id, text: se.bezeichnung };
            });
            this.statuserweiterung.isBusy = false;
            this.statuserweiterung.items = map;
        },
        async fetchContact() {
            this.contact.busy = true;
            const response = await MitgliederApi.getContact(this.id);
            var contactData = [];
            if (response.ok && response.json.collection) {
                contactData = response.json.collection;
                this.contact.items = contactData;
            } else {
                this.contact.items = [];
            }
            this.mergeMissingContactData(contactData);

            this.contact.busy = false;
        },
        async fetchLeitungsteamForMitglied() {
            this.leitungsteam.relations.busy = true;
            const response = await MitgliederApi.getLeitungsteamForMitglied(
                this.id
            );
            if (response.ok) {
                var items = response.json.collection;
                if (items) {
                    this.leitungsteam.relations.items = items;
                } else {
                    this.leitungsteam.relations.items = [];
                }
            }
            this.leitungsteam.relations.busy = false;
        },
        async fetchLeitungsteamGroups() {
            this.leitungsteam.groups.busy = true;
            const response = await MitgliederApi.getLeitungsteamGroups();
            if (response.ok) {
                var items = response.json.collection;
                var options = items.map(item => {
                    return {
                        value: item.id,
                        text: item.description
                    };
                });
                this.leitungsteam.groups.items = items;
                this.leitungsteam.groups.options = this.leitungsteam.groups.options.concat(
                    options
                );
            }
            this.leitungsteam.groups.busy = false;
        },
        async fetchLeitungsteamRoles() {
            this.leitungsteam.roles.busy = true;
            const response = await MitgliederApi.getLeitungsteamRoles();
            if (response.ok) {
                var items = response.json.collection;
                var options = items.map(item => {
                    return {
                        value: item.id,
                        text: item.description
                    };
                });
                this.leitungsteam.roles.items = items;
                this.leitungsteam.roles.options = this.leitungsteam.roles.options.concat(
                    options
                );
            }
            this.leitungsteam.roles.busy = false;
        },
        async fetchMattermost() {
            this.mattermost.busy = true;
            const response = await MitgliederApi.getMattermost(this.id);
            if (response.ok) {
                var json = response.json;
                this.mattermost.email = json.registered_email;
                this.mattermost.status = json.status === "active";
                this.mattermost.initial = false;
            } else {
                this.mattermost.initial = true;
                this.mattermost.email = null;
                this.mattermost.status = false;
            }
            this.mattermost.busy = false;
        },
        cleanPayload(json) {
            Object.keys(json).forEach(key => {
                if (key.startsWith("_")) {
                    delete json[key];
                }
            });
        },
        async dataProvide() {
            this.isBusy = true;

            var response = null;
            if (this.isNew) {
                response = await MitgliederApi.post(this.data);
            } else {
                response = await MitgliederApi.put(this.id, this.data);
            }

            if (!this.isNew) {
                const contactItems = this.contact.items
                    ? this.contact.items
                    : [];
                const contactNewItems = this.contact.newItems
                    ? this.contact.newItems
                    : [];
                const responseContact = await MitgliederApi.putContact(
                    this.id,
                    [...contactItems, ...contactNewItems]
                );

                const responseMattermost = await MitgliederApi.putMattermost(
                    this.id,
                    this.mattermost
                );

                const responseLeitungsteam = await MitgliederApi.putLeitungsteam(
                    this.id,
                    this.leitungsteam.relations.items
                );
            }
            if (response.ok) {
                this.cleanValidationData(this.ioFields);
                if (this.isNew) {
                    if (response.json.id) {
                        this.makeToast(
                            "Erfolgreich gespeichert",
                            "Die Änderungen wurden erfolgreich in der Datenbank gespeichert"
                        );
                        this.$router.push({
                            path: `/mitglieder/${response.json.id}`
                        });
                        this.$emit("update");
                    }
                } else {
                    this.fetchContact();
                    this.fetchMattermost();
                    this.fetchLeitungsteamForMitglied();
                    this.makeToast(
                        "Erfolgreich gespeichert",
                        "Die Änderungen wurden erfolgreich in der Datenbank gespeichert"
                    );
                    this.$emit("update");
                }
            } else {
                this.handleValidationData(response, this.ioFields);
            }
            this.isBusy = false;
        },
        mergeMissingContactData(currentData) {
            // Merging of existing and proposed data sets
            const phoneExisting = currentData.filter(
                item => item.type === "phone" && item.description
            );
            if (phoneExisting.length < 3) {
                const existingPhoneContactTypes = [];
                phoneExisting.forEach(item => {
                    existingPhoneContactTypes.push(
                        item.description.toLowerCase()
                    );
                });
                if (!existingPhoneContactTypes.includes("mobil")) {
                    if (
                        this.contact.items.filter(
                            item =>
                                item.description &&
                                item.description.toLowerCase() === "mobil"
                        ).length < 1
                    ) {
                        this.contact.items.push({
                            id: "new_" + shortid.generate(),
                            description: "Mobil",
                            type: "phone",
                            value: null,
                            mitgied_id: this.id,
                            open: 0,
                            preferred: 0
                        });
                    }
                }
                if (!existingPhoneContactTypes.includes("privat")) {
                    if (
                        this.contact.items.filter(
                            item =>
                                item.description &&
                                item.description.toLowerCase() === "privat"
                        ).length < 1
                    ) {
                        this.contact.items.push({
                            id: "new_" + shortid.generate(),
                            description: "Privat",
                            type: "phone",
                            value: null,
                            mitgied_id: this.id,
                            open: 0,
                            preferred: 0
                        });
                    }
                }
                if (!existingPhoneContactTypes.includes("büro")) {
                    if (
                        this.contact.items.filter(
                            item =>
                                item.description &&
                                item.description.toLowerCase() === "büro"
                        ).length < 1
                    ) {
                        this.contact.items.push({
                            id: "new_" + shortid.generate(),
                            description: "Büro",
                            type: "phone",
                            value: null,
                            mitgied_id: this.id,
                            open: 0,
                            preferred: 0
                        });
                    }
                }
            }

            const mailExisting = currentData.filter(
                item => item.type === "mail" && item.description
            );
            if (mailExisting.length === 0) {
                if (
                    this.contact.items.filter(
                        item =>
                            item.description &&
                            item.description.toLowerCase() === "email"
                    ).length < 1
                ) {
                    this.contact.items.push({
                        id: "new_" + shortid.generate(),
                        description: "Email",
                        type: "mail",
                        value: null,
                        mitgied_id: this.id,
                        open: 0,
                        preferred: 0
                    });
                }
            }
        },
        addMattermostFromMail(item) {
            if (this.mattermost.initial) {
                this.mattermost.email = item.value;
            }
        },
        async checkForPossibleDuplicates() {
            if (this.isNew && this.data) {
                return await this.checkName(this.data.vorname, this.data.name);
            }
            return false;
        }
    }
};
</script>
